/**
 * Pure scoring functions for DCI and IPIP assessments.
 * All functions are stateless — they take data in and return computed results.
 */

import {
  DCI_ITEMS,
  DCI_TOTAL_ITEMS,
  DCI_CATEGORIES,
  DCI_SUBSCALES,
  IPIP_ITEMS,
  IPIP_FACETS,
  IPIP_DOMAINS,
  DOMAIN_FACETS,
  T_SCORE_CATEGORIES,
  MS_CATEGORIES,
  CONVERSION_TABLE,
} from './scoringConfig.js'
import { getNorms } from './normData.js'

/**
 * Reverse-score a Likert item (1-5 scale).
 */
function reverseScore(score) {
  return 6 - score
}

/**
 * Get the scored value for a single item, applying reverse scoring if needed.
 * Returns NaN for non-numeric input (caller should skip null/undefined values).
 */
function scoreItem(rawScore, direction) {
  const score = parseInt(rawScore, 10)
  if (Number.isNaN(score)) return 0
  return direction === 'R' ? reverseScore(score) : score
}

/**
 * Look up which category a value falls into from an ordered threshold array.
 * Each entry: { max, label } — value must be < max (or <= for the last bucket).
 */
function getCategory(value, categories) {
  for (const cat of categories) {
    if (value < cat.max) return cat.label
  }
  return categories[categories.length - 1].label
}

/**
 * Get the T-score category code (VL, ML, A, MH, VH).
 */
function getTScoreCategoryCode(tScore) {
  for (const cat of T_SCORE_CATEGORIES) {
    if (tScore < cat.max) return cat.code
  }
  return T_SCORE_CATEGORIES[T_SCORE_CATEGORIES.length - 1].code
}

// ─── DCI Scoring ────────────────────────────────────────────────────────────

/**
 * Score all DCI items and compute subscale totals and the overall DCI total.
 *
 * @param {Object} likertResponses - Map of questionId → raw score (1-5)
 *   Keys like "dci1_1", "dci2_3", etc.
 * @returns {{
 *   total: number,
 *   category: string,
 *   subscales: Object<string, { total: number, items: string[] }>,
 *   itemScores: Object<string, number>
 * }}
 */
export function scoreDCI(likertResponses) {
  const itemScores = {}
  const subscaleTotals = {}

  // Initialize subscales
  for (const scale of Object.keys(DCI_SUBSCALES)) {
    subscaleTotals[scale] = { total: 0, items: [] }
  }

  // Score each DCI item
  for (const [questionId, config] of Object.entries(DCI_ITEMS)) {
    const raw = likertResponses[questionId]
    if (raw == null) continue

    const scored = scoreItem(raw, config.dir)
    itemScores[questionId] = scored

    if (subscaleTotals[config.scale]) {
      subscaleTotals[config.scale].total += scored
      subscaleTotals[config.scale].items.push(questionId)
    }
  }

  // Compute total (items 1-35, excluding EDC evaluation items)
  let total = 0
  for (const itemId of DCI_TOTAL_ITEMS) {
    if (itemScores[itemId] != null) {
      total += itemScores[itemId]
    }
  }

  const category = getCategory(total, DCI_CATEGORIES)

  return { total, category, subscales: subscaleTotals, itemScores }
}

// ─── IPIP Scoring ───────────────────────────────────────────────────────────

/**
 * Score all IPIP items and compute facet scores, domain scores, Z/T-scores,
 * and converted scores for the MS Score calculation.
 *
 * @param {Object} likertResponses - Map of questionId → raw score (1-5)
 *   Keys like "pi1_1", "pi2_3", etc.
 * @param {string} gender - "M" or "F"
 * @param {number} age - Numeric age
 * @returns {{
 *   facets: Object<string, { score: number, mean: number, sd: number, zScore: number, tScore: number, categoryCode: string, categoryLabel: string, converted: number }>,
 *   domains: Object<string, { score: number, mean: number, sd: number, zScore: number, tScore: number, categoryCode: string, categoryLabel: string }>,
 *   totalConverted: number,
 *   itemScores: Object<string, number>
 * }}
 */
export function scoreIPIP(likertResponses, gender, age) {
  const norms = getNorms(gender, age)
  const itemScores = {}

  // Accumulate facet raw scores
  const facetRawTotals = {}
  for (const facet of Object.keys(IPIP_FACETS)) {
    facetRawTotals[facet] = 0
  }

  // Score each IPIP item
  for (const [questionId, config] of Object.entries(IPIP_ITEMS)) {
    const raw = likertResponses[questionId]
    if (raw == null) continue

    const scored = scoreItem(raw, config.dir)
    itemScores[questionId] = scored
    facetRawTotals[config.facet] += scored
  }

  // Compute facet-level statistics
  const facets = {}
  let totalConverted = 0

  for (const [facetCode, facetName] of Object.entries(IPIP_FACETS)) {
    const score = facetRawTotals[facetCode]
    const normData = norms.facets[facetCode]

    if (!normData) {
      facets[facetCode] = { name: facetName, score, mean: 0, sd: 1, zScore: 0, tScore: 50, categoryCode: 'A', categoryLabel: 'Average', converted: 3 }
      totalConverted += 3
      continue
    }

    const { mean, sd } = normData
    const zScore = sd > 0 ? (score - mean) / sd : 0
    const tScore = 50 + (10 * zScore)
    const categoryCode = getTScoreCategoryCode(tScore)
    const categoryLabel = T_SCORE_CATEGORIES.find(c => c.code === categoryCode)?.label || 'Average'

    // Get the domain for this facet
    const domain = facetCode[0]
    const converted = CONVERSION_TABLE[domain]?.[categoryCode] ?? 3

    facets[facetCode] = {
      name: facetName,
      score,
      mean,
      sd,
      zScore,
      tScore,
      categoryCode,
      categoryLabel,
      converted,
    }
    totalConverted += converted
  }

  // Compute domain-level statistics
  const domains = {}
  for (const [domainCode, domainName] of Object.entries(IPIP_DOMAINS)) {
    const domainFacets = DOMAIN_FACETS[domainCode]
    const score = domainFacets.reduce((sum, f) => sum + facetRawTotals[f], 0)
    const normData = norms.domains[domainCode]

    if (!normData) {
      domains[domainCode] = { name: domainName, score, mean: 0, sd: 1, zScore: 0, tScore: 50, categoryCode: 'A', categoryLabel: 'Average' }
      continue
    }

    const { mean, sd } = normData
    const zScore = sd > 0 ? (score - mean) / sd : 0
    const tScore = 50 + (10 * zScore)
    const categoryCode = getTScoreCategoryCode(tScore)
    const categoryLabel = T_SCORE_CATEGORIES.find(c => c.code === categoryCode)?.label || 'Average'

    domains[domainCode] = {
      name: domainName,
      score,
      mean,
      sd,
      zScore,
      tScore,
      categoryCode,
      categoryLabel,
    }
  }

  return { facets, domains, totalConverted, itemScores }
}

// ─── MS Score ───────────────────────────────────────────────────────────────

/**
 * Compute the Marital Satisfaction Score (MS Score) for a couple.
 *
 * Formula: MS Score = Average IPIP Converted + Individual DCI Total
 * Where Average IPIP Converted = (person1 IPIP Converted + person2 IPIP Converted) / 2
 *
 * @param {{ totalConverted: number }} person1IPIP
 * @param {{ totalConverted: number }} person2IPIP
 * @param {{ total: number }} person1DCI
 * @param {{ total: number }} person2DCI
 * @returns {{
 *   person1: { msScore: number, category: string },
 *   person2: { msScore: number, category: string },
 *   averageIPIPConverted: number
 * }}
 */
export function computeMSScore(person1IPIP, person2IPIP, person1DCI, person2DCI) {
  const averageIPIPConverted = (person1IPIP.totalConverted + person2IPIP.totalConverted) / 2

  const person1MS = averageIPIPConverted + person1DCI.total
  const person2MS = averageIPIPConverted + person2DCI.total

  return {
    person1: {
      msScore: person1MS,
      category: getCategory(person1MS, MS_CATEGORIES),
    },
    person2: {
      msScore: person2MS,
      category: getCategory(person2MS, MS_CATEGORIES),
    },
    averageIPIPConverted,
  }
}
